import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useExperimentVariant } from "@/hooks/useExperimentVariant";
import { LineChart, Lock, Mail, User } from "lucide-react";
import PublicTrendingTicker from "@/components/PublicTrendingTicker";
import TryNowBox from "@/components/TryNowBox";
import SocialLinks from "@/components/SocialLinks";

const SIGNUP_TAGLINE_FALLBACK = {
    key: "five_stocks_clarity",
    render: { line1: "Five stocks.", line2: "Infinite clarity.", color: "#10B981" },
};

const BG_IMAGE =
    "https://static.prod-images.emergentagent.com/jobs/449d5842-eb76-413d-bd7e-07775c2311fa/images/da6e03e36e932405f471d2bb00616910cb10e5c8d593c7714da3cd482bc97e88.png";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function SignupPage() {
    const { user, signup, bootstrapping } = useAuth();
    const navigate = useNavigate();
    const tagline = useExperimentVariant("signup_tagline", SIGNUP_TAGLINE_FALLBACK);
    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    if (!bootstrapping && user) return <Navigate to="/dashboard" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 6) {
            setError("Password must be at least 6 characters");
            return;
        }
        setLoading(true);
        try {
            await signup(email.trim(), password, fullName.trim());
            navigate("/dashboard");
        } catch (err) {
            setError(err?.response?.data?.detail || "Signup failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen w-full relative overflow-hidden" data-testid="signup-page">
            <div
                className="absolute inset-0"
                style={{
                    backgroundImage: `url(${BG_IMAGE})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    filter: "brightness(0.55) saturate(1.1)",
                }}
            />
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(ellipse at 30% 50%, rgba(245,158,11,0.08), transparent 60%), linear-gradient(180deg, rgba(10,12,16,0.55) 0%, rgba(10,12,16,0.88) 100%)",
                }}
            />

            <div className="relative z-10 min-h-screen grid md:grid-cols-2">
                <div className="hidden md:flex flex-col justify-between p-12 lg:p-16">
                    <div className="flex items-center gap-3 text-white">
                        <div
                            style={{
                                width: 34,
                                height: 34,
                                border: "1px solid rgba(255,255,255,0.4)",
                                display: "grid",
                                placeItems: "center",
                            }}
                        >
                            <LineChart size={16} strokeWidth={1.5} />
                        </div>
                        <div className="flex flex-col leading-none">
                            <span className="font-serif text-xl tracking-wide" style={{ letterSpacing: "0.08em", fontWeight: 600 }}>NEULAB</span>
                            <span className="text-overline" style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.58rem" }}>
                                Neural Stock Intelligence&trade;
                            </span>
                        </div>
                    </div>

                    <div className="text-white max-w-lg">
                        <p className="text-overline mb-6" style={{ color: "rgba(255,255,255,0.55)" }}>
                            Begin here
                        </p>
                        <h1
                            className="font-serif hero-number text-white"
                            style={{ fontSize: "clamp(2.4rem, 5vw, 3.8rem)" }}
                            data-testid={`signup-tagline-${tagline.key}`}
                        >
                            {tagline.render.line1}
                            <br />
                            <em className="italic" style={{ color: tagline.render.color }}>
                                {tagline.render.line2}
                            </em>
                        </h1>
                        <p
                            className="mt-8 text-white/70 text-base leading-relaxed max-w-md"
                            style={{ fontFamily: "'Outfit', sans-serif" }}
                        >
                            Your free watchlist covers up to 5 tickers across NASDAQ, NYSE, and
                            SGX. Every analysis cites the data behind its conviction.
                        </p>

                        <div className="mt-8 max-w-xl">
                            <TryNowBox variant="muted" className="mb-3" />
                            <PublicTrendingTicker variant="muted" windowDays={7} limit={8} ctaHref="/signup" ctaLabel="Claim your seat →" />
                        </div>
                    </div>

                    <div
                        className="text-overline space-y-3"
                        style={{ color: "rgba(255,255,255,0.4)" }}
                    >
                        <SocialLinks variant="muted" size="sm" />
                        <p>
                            <a
                                href="https://kidstocks.net"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="signup-footer-kids-link"
                                style={{ color: "rgba(255, 200, 100, 0.85)", textDecoration: "none", marginRight: 14 }}
                            >
                                &#10024; For Kids 8-18 &rarr;
                            </a>
                            <span>&copy; 2026 NeuLab Inc. · Neural Stock Intelligence&trade; · Not financial advice</span>
                        </p>
                    </div>
                </div>

                <div className="flex items-center justify-center p-6 md:p-12">
                    <div
                        className="w-full max-w-md p-8 md:p-10"
                        style={{
                            background: "rgba(10, 12, 16, 0.72)",
                            backdropFilter: "blur(24px)",
                            WebkitBackdropFilter: "blur(24px)",
                            border: "1px solid rgba(255,255,255,0.08)",
                        }}
                        data-testid="signup-card"
                    >
                        <p className="text-overline mb-3" style={{ color: "#94A3B8" }}>
                            Create account
                        </p>
                        <h2 className="font-serif text-white" style={{ fontSize: "2.4rem", lineHeight: 1, letterSpacing: "-0.02em" }}>
                            Open your ledger.
                        </h2>

                        <form onSubmit={onSubmit} className="space-y-4">
                            <div>
                                <label className="text-overline block mb-2" style={{ color: "#94A3B8" }}>
                                    Full name
                                </label>
                                <div className="relative">
                                    <User size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                                    <input
                                        type="text"
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                        required
                                        placeholder="Jane Chen"
                                        className="input-base pl-9"
                                        style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.12)", color: "#fff" }}
                                        data-testid="signup-fullname-input"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-overline block mb-2" style={{ color: "#94A3B8" }}>
                                    Email
                                </label>
                                <div className="relative">
                                    <Mail size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        placeholder="you@firm.com"
                                        className="input-base pl-9"
                                        style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.12)", color: "#fff" }}
                                        data-testid="signup-email-input"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-overline block mb-2" style={{ color: "#94A3B8" }}>
                                    Password
                                </label>
                                <div className="relative">
                                    <Lock size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        placeholder="min. 6 characters"
                                        className="input-base pl-9"
                                        style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.12)", color: "#fff" }}
                                        data-testid="signup-password-input"
                                    />
                                </div>
                            </div>

                            {error && (
                                <div
                                    className="text-sm py-2 px-3 font-mono"
                                    style={{
                                        color: "hsl(var(--sell))",
                                        background: "hsl(var(--sell-bg))",
                                        border: "1px solid hsl(var(--sell-border))",
                                        borderRadius: 2,
                                    }}
                                    data-testid="signup-error"
                                >
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className="btn-primary w-full mt-4"
                                data-testid="signup-submit-button"
                                style={{ background: "#F8FAFC", color: "#0A0C10", borderColor: "#F8FAFC" }}
                            >
                                {loading ? "Creating…" : "Create account"}
                            </button>
                        </form>

                        <p className="text-white/60 text-sm mt-6 text-center" style={{ fontFamily: "'Outfit', sans-serif" }}>
                            Already onboarded?{" "}
                            <Link to="/login" className="link-underline text-white font-medium" data-testid="link-to-login">
                                Sign in
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
